import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
// import printJS from 'print-js'
import { Associate, Patient, PatientBill } from '@app/shared/models';
import { Router } from '@angular/router';
import { Location, DatePipe, CurrencyPipe } from '@angular/common';
import swal from 'sweetalert2';
import { NgxSpinnerService } from 'ngx-spinner';
import { AssociatesService } from '../api/associates.service';
import { AuthService } from '@app/shared/auth/auth.service';
import {
  PRINT_TYPE_BILL,
  PRINT_TYPE_PRESCRIPTION,
  PRINT_TYPE_TREATMENT_PLAN
} from '@app/shared/constants/settings.constants';
import { LEGACY_INDENT_MARGIN_PATTERN } from '@app/shared/constants/print-layout.constants';

@Injectable({
  providedIn: 'root'
})
export class PrintService {
  template: string = 'half-letter';
  templatePage: string;

  htmlToPrint = new BehaviorSubject('-');
  showRx = new BehaviorSubject(false);
  printType: string = 'prescription';
  activePatient = new BehaviorSubject(<Patient>{});
  activeAssociate = new BehaviorSubject({});
  activePrint = new BehaviorSubject(<any>{});
  toPrintData = new BehaviorSubject(<any>{});
  recordDate = new BehaviorSubject({});
  associateId: number = 0;

  isPrinting = false;
  hideSignatures = new BehaviorSubject(<boolean>false);

  public contentReady: boolean = true;
  private readonly printArtifactSelectors: string[] = [
    '.ngb-live',
    '.visually-hidden',
    '.sr-only',
    '.tooltip',
    '.popover',
    '.ngb-typeahead-window',
    '.dropdown-menu.show',
    '.no-print',
    '[aria-live]',
    '[role="status"]',
    '[role="alert"]',
    '[role="tooltip"]',
    'script',
    'style',
    'noscript'
  ];
  private readonly printArtifactTextPattern: RegExp = /^\s*\d+\s+result(?:s)?\s+found\s*$/i;

  /** When true, next {@link print} skips browser/window print (e.g. PDF consent uses merged file in a new tab). */
  public skipBrowserPrintOnce = false;

  constructor(
    private router: Router,
    private location: Location,
    private ngxSpinnerSvc: NgxSpinnerService,
    private assocSvc: AssociatesService,
    private authSvc: AuthService
  ) {}

  /**
   * Escapes HTML special characters to prevent XSS attacks
   * @param text The text to escape
   * @returns The escaped text safe for HTML insertion
   */
  private escapeHtml(text: any): string {
    if (text === null || text === undefined) {
      return '';
    }
    const str = String(text);
    const map: { [key: string]: string } = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#x27;',
      '/': '&#x2F;'
    };
    return str.replace(/[&<>"'\/]/g, (char) => map[char]);
  }

  printDocument(template: string) {
    this.template = template;
    if (template && template == 'letter') {
      this.templatePage = 'genericPrint';
    }
    setTimeout(() => {
      this.isPrinting = true;
      // this.router.navigateByUrl('(print:' + template + ')');
    }, 10);
  }

  onDataReady() {
    if (this.contentReady) {
      this.print();
      return;
    }

    setTimeout(() => {
      this.onDataReady();
    }, 100);
  }

  print() {
    this.ngxSpinnerSvc.hide('printing');
    if (this.skipBrowserPrintOnce) {
      this.skipBrowserPrintOnce = false;
      this.refreshSetFlags();
      return;
    }
    const ua = navigator.userAgent;

    // Define device types
    const isAndroid = /Android/i.test(ua);
    // Android phones include "Mobile" in the UA; tablets do not.
    // Only Android tablets are supported via the popup path — phones fall through to
    // the default "Device Not Supported" case (original behaviour).
    const isAndroidTablet = isAndroid && !/Mobile/i.test(ua);
    const isSmallDevice = /(iPhone|iPad|iPod|CriOS)/i.test(ua) || isAndroidTablet;
    // Exclude Android entirely from the desktop path — Android Chrome contains "Chrome"
    // in the UA which would otherwise match, routing Android phones to window.print()
    // on the Angular SPA (produces blank pages). Android phones should fall through to
    // "Device Not Supported"; Android tablets are already handled by isSmallDevice above.
    const isDesktop = !isSmallDevice && !isAndroid && /(Windows|Macintosh|Chrome|Safari)/i.test(ua);
    const isUnsupportedDevice = /(BlackBerry|IEMobile|Opera Mini)/i.test(ua);

    // If device is unsupported, show alert and return early
    if (isUnsupportedDevice) {
      swal(
        'Device Not Supported',
        'Printing is not supported on this device. Please use an alternative device or contact support for assistance.',
        'error'
      );
      this.refreshSetFlags();
      return;
    }

    // Printing for iOS devices and Android tablets.
    // Android tablets use the popup approach because direct window.print() on the
    // Angular SPA produces blank pages on Android Chrome (media-query rendering issue).
    // Android phones are intentionally excluded — they fall to "Device Not Supported" below.
    if (isSmallDevice) {
      this.printExecuteSmallDevice();
      return;
    }

    // Printing for desktop devices (Windows/Mac)
    if (isDesktop) {
      this.prepareForBrowserPrint();
      const onPrintDialogClosed = () => {
        this.restoreAfterBrowserPrint();
        window.removeEventListener('afterprint', onPrintDialogClosed);
      };
      window.addEventListener('afterprint', onPrintDialogClosed);
      setTimeout(() => {
        window.print();
      }, 1);
      this.refreshSetFlags();
      return;
    }

    // Default case: unsupported devices not caught by `isUnsupportedDevice`
    swal(
      'Device Not Supported',
      'Printing is not supported on this device. Please use an alternative device or contact support for assistance.',
      'error'
    );
    this.refreshSetFlags();
  }

  printData(htmlString: string, template?: string) {
    this.ngxSpinnerSvc.show('printing');
    if (!template) {
      template = 'half-letter';
    }
    this.htmlToPrint.next(this.sanitizePrintMarkup(htmlString));
    setTimeout(() => {
      this.printDocument(template);
    }, 1);
  }

  printStandaloneData(htmlString: string, template: string = 'letter'): void {
    // Generic reports must not inherit identifying data from an earlier patient print.
    this.activePatient.next(<Patient>{});
    this.activeAssociate.next({});
    this.activePrint.next({});
    this.printData(htmlString, template);
  }

  printToLetter(page: string) {
    this.ngxSpinnerSvc.show('printing');
    this.template = 'letter';
    this.templatePage = page;
    setTimeout(() => {
      this.isPrinting = true;
      // this.router.navigateByUrl('(print:letter/' + page + ')');
    }, 10);
    // }, 100)
  }

  printTreatmentPlan(printOptions: any) {
    const plan: any = printOptions.plan;
    const patient: Patient = printOptions.patient;
    const useFixedAmountDiscount: any = printOptions.useFixedAmountDiscount;
    const template: string = printOptions.template ? printOptions.template : 'half-letter';

    const cPipe: CurrencyPipe = new CurrencyPipe('en');
    this.associateId = plan.associateId;
    // set patient
    this.activePatient.next(patient);
    // this.activeAssociate.next(plan.associate);
    this.activePrint.next(PRINT_TYPE_TREATMENT_PLAN);

    let totalCost = 0;
    let discountP = 0;
    let discountAmount = parseFloat(plan.discountAmount);
    let netCost = 0;
    let colspan: number = 2;
    const showRowPrice: boolean = plan.showRowPrice;
    const hasServiceDiscount: boolean = (plan.details || []).some(
      (tpd: any) => (parseFloat(tpd.serviceDiscountAmount) || 0) > 0 || (parseFloat(tpd.serviceDiscountPercent) || 0) > 0
    );
    const showDiscountColumn: boolean = showRowPrice && hasServiceDiscount;
    let plans: any = [];
    let idCounter = 0; // Initialize ID counter

    plan.details.forEach((tpd: any, index: number) => {
      const apptIndex = index; // Use iteration index as the index
      if (!plans[apptIndex]) {
        plans[apptIndex] = {
          id: idCounter++,
          total_cost: 0,
          discount_percent: parseFloat(plan.discountPercent),
          appt_no: tpd.apptNo,
          details: []
        };
      }
      plans[apptIndex].details.push(tpd);
      plans[apptIndex].total_cost += parseFloat(tpd.cost);
    });
    let html: string = '<h3 align="center" class="page-title">Treatment Plan</h3><div class="print-treatment-plan">';
    html += '<table class="table table-bordered print-table" >';
    // html += '<thead><tr><th width="70">Tooth #</th><th>Procedure</th></tr></thead><tbody>';
    html += '<thead><tr><th width="60">Appt </th><th width="70">Tooth #</th><th>Procedure</th>';
    if (showRowPrice) {
      html += '<th>Unit Price</th><th>Subtotal</th>';
      colspan = 5; // Appt + Tooth # + Procedure + Unit Price + Subtotal
      if (showDiscountColumn) {
        const discountHeader = useFixedAmountDiscount ? 'Discount' : 'Discount %';
        html += '<th>' + discountHeader + '</th>';
        colspan = 6; // ... + Discount
      }
      html += '<th>Est. Cost</th>';
    } else {
      html += '<th>Est. Cost</th>';
      colspan = 3; // Appt + Tooth # + Procedure
    }
    html += '</tr></thead><tbody>';
    let prevApptNo = '';
    let subtotal = 0; // Initialize subtotal for each appt_no
    plans.forEach((plan, idx) => {
      if (plan.details.length > 0) {
        if (plan.appt_no !== prevApptNo) {
          if (prevApptNo !== '') {
            html +=
              '<tr><td colspan="' +
              colspan +
              '" align="right"><strong>Subtotal: </strong></td><td align="right"><strong>' +
              cPipe.transform(subtotal.toFixed(2), ' ') +
              '</strong></td></tr>';
            html += '<tr><td colspan="' + colspan + '" align="right"> </td><td> </td></tr>';
          }

          prevApptNo = plan.appt_no;
          subtotal = 0; // Reset subtotal for the new appt_no
          html += '<tr><td rowspan="' + plan.details.length + '">' + plan.appt_no + '</td>';
        } else {
          html += '<tr><td></td>';
        }

        let detail = plan.details[0];
        html += '<td>';
        for (let i = 0; i < detail.toothNo.length; i++) {
          html += '<span>' + this.escapeHtml(detail.toothNo[i]) + '</span>';
        }
        html += '</td>';
        html += '<td>' + this.escapeHtml(detail.planProcedure) + '</td>';
        if (showRowPrice) {
          const unitPrice = parseFloat(detail.unitPrice ?? detail.unit_price ?? 0);
          const subTotal0 = parseFloat(detail.subTotal) || parseFloat(detail.cost);
          html += '<td align="right">' + cPipe.transform(unitPrice.toFixed(2), ' ') + '</td>';
          html += '<td align="right">' + cPipe.transform(subTotal0.toFixed(2), ' ') + '</td>';
          if (showDiscountColumn) {
            const discountDisplay0 = useFixedAmountDiscount
              ? cPipe.transform((parseFloat(detail.serviceDiscountAmount) || 0).toFixed(2), ' ')
              : (parseFloat(detail.serviceDiscountPercent) || 0).toFixed(2) + '%';
            html += '<td align="right">' + discountDisplay0 + '</td>';
          }
          html += '<td align="right">' + cPipe.transform(parseFloat(detail.cost).toFixed(2), ' ') + '</td>';
        } else {
          html += '<td align="center"> - </td>';
        }
        subtotal += parseFloat(detail.cost); // Add to subtotal
        html += '</tr>';
        totalCost += parseFloat(plan.total_cost);
        netCost += parseFloat(plan.net_cost);
        discountP = parseFloat(plan.discount_percent);

        for (let x = 1; x < plan.details.length; x++) {
          detail = plan.details[x];
          html += '<tr>';
          html += '<td>';
          for (let i = 0; i < detail.toothNo.length; i++) {
            html += '<span>' + this.escapeHtml(detail.toothNo[i]) + '</span>';
          }
          html += '</td>';
          html += '<td>' + this.escapeHtml(detail.planProcedure) + '</td>';
          if (showRowPrice) {
            const unitPrice = parseFloat(detail.unitPrice ?? detail.unit_price ?? 0);
            const subTotalX = parseFloat(detail.subTotal) || parseFloat(detail.cost);
            html += '<td align="right">' + cPipe.transform(unitPrice.toFixed(2), ' ') + '</td>';
            html += '<td align="right">' + cPipe.transform(subTotalX.toFixed(2), ' ') + '</td>';
            if (showDiscountColumn) {
              const discountDisplayX = useFixedAmountDiscount
                ? cPipe.transform((parseFloat(detail.serviceDiscountAmount) || 0).toFixed(2), ' ')
                : (parseFloat(detail.serviceDiscountPercent) || 0).toFixed(2) + '%';
              html += '<td align="right">' + discountDisplayX + '</td>';
            }
            html += '<td align="right">' + cPipe.transform(parseFloat(detail.cost).toFixed(2), ' ') + '</td>';
          } else {
            html += '<td align="center"> - </td>';
          }
          subtotal += parseFloat(detail.cost); // Add to subtotal
          html += '</tr>';
        }
      }
    });
    // Add the final subtotal row
    if (prevApptNo !== '') {
      html +=
        '<tr><td colspan="' +
        colspan +
        '" align="right"><strong>Subtotal: </strong></td><td align="right"><strong>' +
        cPipe.transform(subtotal.toFixed(2), ' ') +
        '</strong></td></tr>';
      html += '<tr><td colspan="' + colspan + '" align="right"> </td><td> </td></tr>';
    }
    if (useFixedAmountDiscount) {
      netCost = totalCost - discountAmount;
    } else {
      netCost = totalCost - totalCost * (discountP / 100);
    }
    html += '</tbody>';
    html += '<tfoot><tr><td colspan="' + (colspan + 1) + '">';
    html +=
      '<div align="right" style="display:block;padding:5px;"> <strong>Estimated Cost: ' +
      cPipe.transform(totalCost, ' ') +
      '</strong></div>';

    if (discountP > 0 || discountAmount > 0) {
      if (useFixedAmountDiscount) {
        html +=
          '<div align="right" style="display:block;padding:5px;"><strong>Discount Amount: ' +
          cPipe.transform(discountAmount, ' ') +
          '</strong></div><div align="right" style="display:block;padding:5px;"><strong>Net Estimated Cost: ' +
          cPipe.transform(netCost, ' ') +
          '</strong></div>';
      } else {
        if (totalCost - totalCost * discountP != plan.netCost) {
          let exactDiscountP = (discountAmount / totalCost) * 100;
          let exactNetCost = totalCost - totalCost * (exactDiscountP / 100);
          html +=
            '<div align="right" style="display:block;padding:5px;"><strong>Discount (%): ' +
            exactDiscountP.toFixed(4) +
            '</strong></div><div align="right" style="display:block;padding:5px;"><strong>Net Estimated Cost: ' +
            cPipe.transform(exactNetCost, ' ') +
            '</strong></div>';
        } else {
          html +=
            '<div align="right" style="display:block;padding:5px;"><strong>Discount (%): ' +
            cPipe.transform(discountP, ' ') +
            '</strong></div><div align="right" style="display:block;padding:5px;"><strong>Net Estimated Cost: ' +
            cPipe.transform(netCost, ' ') +
            '</strong></div>';
        }
      }
    }
    html += '</td></tr> </tfoot></table>';
    html +=
      '<div> <span style="color:red;" class="text-danger danger">**Prices are subject to change without prior notice.</span> </div>';
    html +=
      '<br><div><b><h3>Remarks/Notes</h3></b><br><span >' + this.escapeHtml(plan.details[0].remarks) + '</span></div>';
    html += '</div>';

    return this.printData(html, template);
  }

  printBill(bill: any, billDetails: any, patient: Patient, useFixedAmountDiscounts?: boolean) {
    let totalPaid: number = 0;
    const isFixed = useFixedAmountDiscounts ?? false;

    billDetails.payments.forEach((p: any) => {
      totalPaid += Number(p.paid_amount);
    });

    // Calculate Account Credit Applications total
    let totalAccountCredits: number = 0;
    if (bill.billAccountCreditApplications && bill.billAccountCreditApplications.length > 0) {
      totalAccountCredits = bill.billAccountCreditApplications.reduce((sum: number, acc: any) => {
        return sum + (Number(acc.appliedAmount) || 0);
      }, 0);
    }

    let balanceAmount: number = bill.netAmount - totalPaid - totalAccountCredits;
    if (balanceAmount < 0) {
      balanceAmount = 0;
    }

    const hasAnyDetailDiscount =
      billDetails.details?.some(
        (bd: any) => Number(bd.discount_amount) > 0 || Number(bd.discount_percentage) > 0
      ) ?? false;
    const labelColspan = hasAnyDetailDiscount ? 5 : 4;

    this.activeAssociate.next(bill.associate);
    this.activePrint.next(PRINT_TYPE_BILL);
    this.activePatient.next(patient);
    let discountTitle = isFixed ? 'Discount' : 'Discount %';
    // set bill details
    let html: string = '<div class="print-bill" style="--mmp-table-header:#ffffff;--mmp-table-row:#ffffff;--mmp-table-row-alt:#f9fafb;--mmp-text:#111827;--mmp-text-soft:#374151;">';
    html +=
      '<h3 align="center" class="page-title">Patient Bill on ' +
      new DatePipe('en').transform(bill.billDt, 'mediumDate') +
      '</h3>';
    html += '<table class="table table-bordered table-striped bill-print">';
    html +=
      '<thead><tr><th>Service/Procedure</th><th>Teeth</th><th>Qty</th><th>Base Amount</th>' +
      (hasAnyDetailDiscount ? '<th>' + discountTitle + '</th>' : '') +
      '<th align="right" class="text-right">Total Amount</th></tr></thead><tbody>';
    billDetails.details.forEach((bd) => {
      html += '<tr>';
      html += '<td>' + bd.service_type + '</td>';
      html += '<td>' + bd.item + '</td>';
      html += '<td>' + bd.qty + '</td>';
      html += '<td align="right">' + new CurrencyPipe('en').transform(bd.unit_price, '₱') + '</td>';
      if (hasAnyDetailDiscount) {
        html += '<td align="right">' + (isFixed ? new CurrencyPipe('en').transform(bd.discount_amount, '₱') : bd.discount_percentage) + '</td>';
      }
      html += '<td align="right">' + new CurrencyPipe('en').transform(bd.total_amount, '₱') + '</td>';
      html += '</tr>';
    });
    html += '</tbody><tfoot>';
    html +=
      '<tr><td colspan="' + labelColspan + '">SubTotal</td><td align="right">' +
      new CurrencyPipe('en').transform(bill.grossAmount, 'P') +
      '</td></tr>';
    if (bill.discountAmount > 0) {
      html +=
        '<tr><td colspan="' + labelColspan + '">Discount</td><td align="right">' +
        new CurrencyPipe('en').transform(bill.discountAmount, 'P') +
        '</td></tr>';
    }
    if (totalPaid > 0) {
      html +=
        '<tr><td colspan="' + labelColspan + '">Paid Amt</td><td align="right" class="paid" style="color:green">' +
        new CurrencyPipe('en').transform(totalPaid, 'P') +
        '</td></tr>';
    }
    if (totalAccountCredits > 0) {
      html +=
        '<tr><td colspan="' + labelColspan + '">Account Credit Applications</td><td align="right" class="account-credits" style="color:#0055cc">' +
        new CurrencyPipe('en').transform(totalAccountCredits, 'P') +
        '</td></tr>';
    }
    html +=
      '<tr><td colspan="' + labelColspan + '">Balance</td><td align="right" class="balance" style="color:red">' +
      new CurrencyPipe('en').transform(balanceAmount, 'P') +
      '</td></tr>';
    html += '</tfoot></table>';

    if (billDetails.payments && billDetails.payments.length > 0) {
      html += '<h4 align="left">Payments</h4>';
      html += '<table class="table table-bordered table-striped bill-print">';
      html +=
        '<thead><tr><th width="100">Date</th><th>Details</th><th align="right" class="text-right">Total</th></tr></thead><tbody>';

      billDetails.payments.forEach((p) => {
        html += '<tr>';
        html += '<td>' + new DatePipe('en').transform(p.payment_dt, 'mediumDate') + '</td>';
        html += '<td>';

        const pdetails: any = p.payment_details;
        if (pdetails && pdetails.length > 0) {
          pdetails.forEach((pd) => {
            html += '<div><span class="text-capitalize">' + this.escapeHtml(pd.payment_type) + '</span> &nbsp;&nbsp;';
            html += '<span align="right">' + new CurrencyPipe('en').transform(pd.paid_amount, 'P') + '</span></div>';
          });
        }
        if (p.remarks.trim().length > 1) {
          html += '<div>(Remarks: ' + this.escapeHtml(p.remarks) + ')</div>';
        }

        html += '</td>';
        html += '<td align="right">' + new CurrencyPipe('en').transform(p.paid_amount, 'P') + '</td>';
        html += '</tr>';
      });

      html += '</tbody><tfoot>';
      html += '</tfoot></table>';
    }

    // Add Account Credit Applications
    if (bill.billAccountCreditApplications && bill.billAccountCreditApplications.length > 0) {
      html += '<h4 align="left">Account Credit Applications</h4>';
      html += '<table class="table table-bordered table-striped bill-print">';
      html +=
        '<thead><tr><th width="100">Date</th><th>Details</th><th align="right" class="text-right">Amount</th></tr></thead><tbody>';

      bill.billAccountCreditApplications.forEach((accountCredit: any) => {
        html += '<tr>';
        html += '<td>' + new DatePipe('en').transform(accountCredit.appliedAt, 'mediumDate') + '</td>';
        html += '<td>';

        let accountCreditDetailsText: string[] = [];
        accountCreditDetailsText.push('<div>Account Credit Applied</div>');
        if (accountCredit.patientAccountCredit) {
          accountCreditDetailsText.push('<div>Credit ID: ' + accountCredit.patientAccountCredit.id + '</div>');
        }
        if (accountCredit.remarks) {
          accountCreditDetailsText.push('<div>(Remarks: ' + accountCredit.remarks + ')</div>');
        }

        html += accountCreditDetailsText.join('');
        html += '</td>';
        html += '<td align="right">' + new CurrencyPipe('en').transform(accountCredit.appliedAmount, 'P') + '</td>';
        html += '</tr>';
      });

      html += '</tbody><tfoot>';
      html += '</tfoot></table>';
    }

    if (billDetails.details.length <= 2) {
      html += '<br><br>';
    }
    html += '</div>';

    return this.printData(html);
  }

  printPrescription(medicines: any[], patient: Patient, remarks?: string, associate?: any) {
    // set patient
    this.activePatient.next(patient);
    this.activeAssociate.next(associate);
    this.activePrint.next(PRINT_TYPE_PRESCRIPTION);

    this.showRx.next(true);
    // set prescription details
    let html: string = '<div class="print-prescription">';
    medicines.forEach((med, k) => {
      html += '<div class="pdetail">';
      // html += '<div class="quantity"> x ' + med.quantity + '</div>';
      html +=
        '<div class="medicine"><span class="med">' +
        (k + 1).toString() +
        '.) ' +
        this.escapeHtml(med.medicine) +
        '</span><span class="quantity"> # ' +
        this.escapeHtml(med.quantity) +
        '</span></div>';
      html += '<div class="dosage"><i>Sig:</i> ' + this.escapeHtml(med.dosage) + '</div>';
      html += '</div>';
    });
    if (remarks && remarks.trim() != '') {
      html += '<div><br>';
      html += this.escapeHtml(remarks);
      html += '</div>';
    }
    if (medicines.length === 1) {
      html += '<br><br><br><br><br><br>';
    } else if (medicines.length === 2) {
      html += '<br><br><br><br>';
    }
    html += '</div>';
    return this.printData(html);
  }

  private refreshSetFlags() {
    setTimeout(() => {
      this.isPrinting = false;
      this.showRx.next(false);
      this.hideSignatures.next(false);
      this.contentReady = true;
      this.htmlToPrint.next('-');
      this.recordDate.next({});
      // this.location.back();
    }, 1000);
  }

  private createPrintHTML(htmlContent, pageSize, orientation, width, height) {
    const version = new Date().getTime();
    const templateClass = this.template === 'half-letter' ? 'half-letter' : 'letter';
    return `<html>
            <head>
              <title>Print</title>
              <link rel="stylesheet" type="text/css" href="/assets/css/print-inline.css?v=${version}">
              <style>
                body{ overflow-x: hidden; }
                /* Do NOT hide body divs here — Android Chrome does not reliably apply
                   @media print before generating the print preview, which would leave all
                   content hidden and produce blank pages. Content is always visible so the
                   OS print dialog can render it regardless of @media print support. */
                @page{ size: ${pageSize} ${orientation}; }
                @media print {
                  body{ display:block; }
                  body div{ display:block; }
                  body,html { height: 100%;margin: 0; }
                  .print-container { display: flex; flex-direction: column;}
                  .head-details{
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 10px 0;
                  }
                  .head-details .logo-section {
                    flex: 0 0 auto;
                    display: flex;
                    align-items: center;
                    gap: 15px;
                  }
                  .head-details .contact-section {
                    flex: 0 0 auto;
                    text-align: left;
                    font-size: 12px;
                    line-height: 1.4;
                  }
                  .header { flex: 0 0 auto; }
                  .content { flex: 1 1 auto; overflow: auto; }
                  .patient-details { display: flex; }
                  .flex-left { flex: 1; }
                  .flex-right { flex-shrink: 0;padding-right: 10px; }
                  .footer { page-break-before: avoid; page-break-inside: avoid; display: flex; flex: 0 0 auto;}
                  .signature { display: block; max-width: 150px !important; }
                  .signature img { max-width: 150px; image-rendering: -webkit-optimize-contrast; image-rendering: crisp-edges; }
                  /* Enhanced image quality settings for header images */
                  .head-details img {
                    max-width: 520px;
                    height: auto;
                    image-rendering: -webkit-optimize-contrast;
                    image-rendering: crisp-edges;
                    /* For better quality, use higher resolution limits */
                    /* Ensure we don't exceed maximum dimensions while preserving quality */
                    /* Calculate height to maintain aspect ratio (assuming original is roughly 2:1 ratio) */
                    /* If logo has height property, use it; otherwise calculate based on typical header proportions */
                    object-fit: contain;
                    object-position: center;
                  }
                  /* Half-size template specific styling */
                  .half-letter .head-details img {
                    max-width: 340px;
                    max-height: 120px;
                  }
                  /* Full-size template specific styling */
                  .letter .head-details img {
                    max-width: 520px;
                    max-height: 180px;
                  }
                  .text-danger, .danger { color: red; }
                  /* Allow Quill background-color highlights to render.
                     This popup window has no app-root / app-print-* selectors so
                     print-inline.css rules don't apply here — we set
                     print-color-adjust directly on the content wrapper. */
                  .body-details {
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                  }
                  /* print-inline.css sets br { height: 100px !important } globally for the
                     Angular SPA print overlay. In this popup window that rule inflates <br>
                     spacers to 100px each, causing Android's print renderer to hang while
                     paginating a document whose content height far exceeds the page size.
                     Override it here — this <style> tag is later in <head> than the <link>,
                     so it wins cascade even against !important at equal specificity. */
                  br { height: auto !important; display: inline !important; }
                }
                .page-size-wrapper { width: ${width};  height: ${height}; }
              </style>
            </head>
            <body>
              <div class="page-size-wrapper ${templateClass}">${htmlContent}</div>
            </body>
          </html>`;
  }

  private removeArtifactTextNodes(root: HTMLElement): void {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodesToRemove: Text[] = [];
    let currentNode = walker.nextNode();

    while (currentNode) {
      const textNode = currentNode as Text;
      if (this.printArtifactTextPattern.test(textNode.textContent || '')) {
        nodesToRemove.push(textNode);
      }
      currentNode = walker.nextNode();
    }

    nodesToRemove.forEach((node) => node.remove());
  }

  /**
   * Quill colour preservation is now handled entirely in print-inline.css:
   * the blanket `color:#000 !important` and `background:#fff !important` rules
   * were scoped to structural sections (.header, .footer, .patient-details) and
   * excluded from .body-details.  Inside .body-details, Quill's inline
   * style="color:..." / style="background-color:..." attributes win through
   * normal CSS cascade (inline > non-!important class selector).
   * print-color-adjust:exact is set on .body-details in print-inline.css so
   * background highlights render in the print dialog without extra JS.
   * These methods are intentionally kept as stubs so call-sites in
   * prepareForBrowserPrint / restoreAfterBrowserPrint need no changes.
   */
  private preserveQuillColorsForPrint(): void { /* handled by CSS */ }
  private restoreQuillColorsAfterPrint(): void { /* handled by CSS */ }

  /** Close open row-action menus before print without leaving permanent inline styles. */
  private prepareForBrowserPrint(): void {
    document.querySelectorAll('.dropdown-menu.show').forEach((node) => {
      node.classList.remove('show');
    });
    document.querySelectorAll('.dropdown.show').forEach((node) => {
      node.classList.remove('show');
    });
    document.querySelectorAll('[ngbdropdowntoggle][aria-expanded="true"], .dropdown-toggle[aria-expanded="true"]').forEach((node) => {
      node.setAttribute('aria-expanded', 'false');
    });
    this.preserveQuillColorsForPrint();
  }

  /** Safety net if a prior print attempt left menus in a bad state. */
  private restoreAfterBrowserPrint(): void {
    document.querySelectorAll('.dropdown-menu, .patient-action-menu, .recall-list-actions-menu, .bill-actions-menu').forEach((node) => {
      if (!(node instanceof HTMLElement)) {
        return;
      }
      node.style.removeProperty('display');
      node.removeAttribute('aria-hidden');
    });
    this.restoreQuillColorsAfterPrint();
  }

  private sanitizePrintMarkup(htmlString: string): string {
    if (!htmlString || !htmlString.trim()) {
      return '';
    }
    const parsed = new DOMParser().parseFromString(htmlString, 'text/html');
    const body = parsed.body;

    this.printArtifactSelectors.forEach((selector) => {
      body.querySelectorAll(selector).forEach((node) => node.remove());
    });
    this.removeArtifactTextNodes(body);
    this.stripLegacyIndentMargins(body);
    return body.innerHTML;
  }

  /**
   * Removes the inline `margin-left: <n>em` that the old `resolveQuillIndent()`
   * baked into saved certificate records.
   *
   * It stacks with the `.ql-indent-*` padding rules, so indented paragraphs
   * printed at exactly twice the editor's indent (measured 0/96/192px against
   * the editor's 0/48/96px). Stripping at render time means records saved before
   * and after the fix print identically, with no data migration.
   */
  private stripLegacyIndentMargins(root: HTMLElement): void {
    root.querySelectorAll('[class*="ql-indent-"]').forEach((node) => {
      const element = node as HTMLElement;
      const style = element.getAttribute('style');
      if (!style) {
        return;
      }
      const cleaned = style.replace(LEGACY_INDENT_MARGIN_PATTERN, '').trim();
      if (cleaned) {
        element.setAttribute('style', cleaned);
      } else {
        element.removeAttribute('style');
      }
    });
  }

  private getSanitizedPrintContainerHtml(): string {
    const printContainer = document.querySelector('.print-container.ql-snow') as HTMLElement | null;
    if (!printContainer) {
      return '';
    }
    const clone = printContainer.cloneNode(true) as HTMLElement;
    this.printArtifactSelectors.forEach((selector) => {
      clone.querySelectorAll(selector).forEach((node) => node.remove());
    });
    this.removeArtifactTextNodes(clone);
    return clone.outerHTML;
  }

  private printExecuteSmallDevice() {
    setTimeout(() => {
      const ua = navigator.userAgent;
      const isSmallDevice = /(iPhone|iPad|iPod|CriOS)/i.test(ua);
      const htmlContent = this.getSanitizedPrintContainerHtml();
      if (!htmlContent) {
        swal('Unable to Print', 'Printable content was not found. Please try again.', 'error');
        this.refreshSetFlags();
        return;
      }

      // Open a new window or tab
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        swal('Unable to Print', 'Pop-up was blocked. Please allow pop-ups and try again.', 'error');
        this.refreshSetFlags();
        return;
      }
      const pageSize = this.template === 'half-letter' ? '5.5in 8.5in' : '8.5in 11in';
      const orientation = this.template === 'half-letter' ? 'landscape' : 'portrait';
      const [width, height] = pageSize.split(' ');

      // Write the HTML content to the new window or tab
      printWindow.document.write(this.createPrintHTML(htmlContent, pageSize, orientation, width, height));
      printWindow.document.close();

      // Wait for all content to load before printing
      printWindow.onload = () => {
        if (isSmallDevice) {
          if (typeof printWindow.print !== 'function') {
            let printButton = printWindow.document.createElement('button');
            printButton.textContent = 'Print';
            printButton.onclick = () => printWindow.print();
            printWindow.document.body.appendChild(printButton);
          } else {
            printWindow.print();
          }
        } else {
          printWindow.print();
        }
        printWindow.onafterprint = () => {
          printWindow.close();
        };
      };
    }, 1);
    this.refreshSetFlags();
  }
}
